// Shared preventative-maintenance data helpers.
//
// Previously each PM report (PMReportButton, PMValidationReport,
// PMReportButton_FillTemplate) re-implemented date parsing, range checks, and
// PM-check collection/fetching. These are the single source of truth.
import { getDoc } from "firebase/firestore";
import { propertyDoc } from "../firestorePaths";

/** Parse a "YYYY-MM-DD" or ISO string into a Date, or null if invalid. */
export const parseISO = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

/** Start of the given day (00:00:00.000). */
export const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** End of the given day (23:59:59.999) so a range is inclusive of the end date. */
export const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

/** True if d falls within [start, end] inclusive. */
export const isWithinRangeInclusive = (d, start, end) =>
  !!d && d.getTime() >= start.getTime() && d.getTime() <= end.getTime();

/**
 * Collect all PM-check references from rooms whose date falls in [start, end].
 * Accepts the rooms array from PropertyContext (or an object keyed by id).
 *
 * Each entry: { unitId, roomName, pmCheckId, pmDate }
 *  - unitId   = room.id (the Firestore unit doc id; correct path segment)
 *  - roomName = room.displayName (for cover pages / labels)
 */
export const collectPMChecks = (rooms, start, end) => {
  const out = [];
  const list = Array.isArray(rooms) ? rooms : Object.values(rooms || {});
  list.forEach((room) => {
    const hist = room?.preventativeMaintenanceHistory || [];
    hist.forEach((h) => {
      const d = parseISO(h?.date);
      if (d && h?.pmCheckId && isWithinRangeInclusive(d, start, end)) {
        out.push({
          unitId: room.id,
          roomName: room?.displayName || room.id,
          pmCheckId: String(h.pmCheckId),
          pmDate: d,
        });
      }
    });
  });
  out.sort(
    (a, b) => a.pmDate - b.pmDate || a.roomName.localeCompare(b.roomName)
  );
  return out;
};

/**
 * Fetch the pmChecks document for each collected check.
 * Returns the same entries with an added `pmDoc` (or null if missing).
 */
export const fetchPMChecks = async (db, clientId, propertyId, checks) =>
  Promise.all(
    checks.map(async (m) => {
      const ref = propertyDoc(
        clientId,
        propertyId,
        "units",
        m.unitId,
        "pmChecks",
        m.pmCheckId
      );
      const snap = await getDoc(ref);
      return {
        ...m,
        pmDoc: snap.exists() ? { id: snap.id, ...snap.data() } : null,
      };
    })
  );
